import { useCallback, useRef } from "react";
import { useTrackedSessionStorage } from "./use-tracked-browser-storage";

// Benchmark cohort definition mode
export type BenchmarkCohortMode = "dynamic" | "static";

// BenchmarkConfig stored in sessionStorage (page-level, not per-campaign)
export interface BenchmarkConfig {
  filters: Record<string, unknown>;
  campaignCount: number;
  excludeCurrentCampaign: boolean;
  lastUpdated: string | null;
  mode: BenchmarkCohortMode;
  selectedCampaignIds?: string[]; // For static mode: explicit list of campaign IDs
}

// Benchmark enabled state (page-level boolean in sessionStorage)
type BenchmarkEnabledState = boolean;

export function useBenchmarkConfig() {
  const isSaving = useRef(false);
  const isClearing = useRef(false);

  // Store benchmark config in sessionStorage (page-level, shared across campaigns within the session)
  const [config, setConfig] = useTrackedSessionStorage<BenchmarkConfig | null>(
    "campaigns::explorer::benchmark::config",
    null,
  );

  // Store benchmark enabled state as a simple boolean in sessionStorage (tab-specific)
  const [enabledState, setEnabledState] =
    useTrackedSessionStorage<BenchmarkEnabledState>(
      "campaigns::explorer::benchmark::enabled",
      false,
    );

  // Default empty config wrapped in useRef to maintain reference stability
  const defaultConfig = useRef<BenchmarkConfig>({
    filters: {},
    campaignCount: 0,
    excludeCurrentCampaign: true,
    lastUpdated: null,
    mode: "static",
    selectedCampaignIds: [],
  }).current;

  // Resolve current config (fallback to default when empty)
  const resolvedConfig = config || defaultConfig;

  // Enabled state is a simple boolean
  const isEnabled = enabledState === true;

  // Save benchmark config (page-level)
  const saveBenchmarkConfig = useCallback(
    async (
      filters: Record<string, unknown>,
      campaignCount: number,
      excludeCurrentCampaign: boolean = true,
      mode: BenchmarkCohortMode = "static",
      selectedCampaignIds: string[] = [],
    ) => {
      // Skip if already saving
      if (isSaving.current) return;

      isSaving.current = true;

      try {
        // Determine if we're clearing the config
        const isEmptyConfig =
          Object.keys(filters).length === 0 && campaignCount === 0;
        isClearing.current = isEmptyConfig;

        // Create a new config object (to avoid reference issues)
        const newConfig: BenchmarkConfig = {
          // Make a deep copy of filters
          filters: isEmptyConfig ? {} : JSON.parse(JSON.stringify(filters)),
          // Set campaign count
          campaignCount: isEmptyConfig ? 0 : campaignCount,
          // Set exclude flag
          excludeCurrentCampaign: isEmptyConfig ? true : excludeCurrentCampaign,
          // Update timestamp for valid configs, null for cleared configs
          lastUpdated: isEmptyConfig ? null : new Date().toISOString(),
          // Set mode, default to static for cleared configs
          mode: isEmptyConfig ? "static" : mode,
          // Set selected campaign IDs for static mode, empty array otherwise
          selectedCampaignIds: isEmptyConfig
            ? []
            : mode === "static"
              ? [...selectedCampaignIds]
              : [],
        };

        // When clearing config, also disable benchmark mode
        if (isEmptyConfig) {
          setEnabledState(false);
        }

        // Update the page-level config
        setConfig(newConfig);
      } finally {
        // Reset flags after a delay
        setTimeout(() => {
          isSaving.current = false;
          isClearing.current = false;
        }, 50);
      }
    },
    [setConfig, setEnabledState],
  );

  // Update just the enabled state (now only handles sessionStorage)
  const setBenchmarkEnabled = useCallback(
    (enabled: boolean) => {
      if (isSaving.current) {
        return;
      }
      isSaving.current = true;
      try {
        setEnabledState(enabled);
      } finally {
        setTimeout(() => {
          isSaving.current = false;
        }, 50);
      }
    },
    [setEnabledState],
  );

  // Clear benchmark config method
  const clearBenchmarkConfig = useCallback(async () => {
    // Skip if already clearing
    if (isClearing.current) return;

    isClearing.current = true;

    try {
      // Clear the page-level config
      setConfig(null);
      // Also disable benchmark mode when clearing config
      setEnabledState(false);
    } finally {
      // Reset flag after a delay
      setTimeout(() => {
        isClearing.current = false;
      }, 50);
    }
  }, [setConfig, setEnabledState]);

  return {
    benchmarkConfig: resolvedConfig,
    isBenchmarkEnabled: isEnabled,
    saveBenchmarkConfig,
    clearBenchmarkConfig,
    setBenchmarkEnabled,
    hasBenchmarkConfig: Boolean(resolvedConfig?.lastUpdated),
  };
}
